import React from 'react'
import { Button } from '@wove/web-ui'
import { Edit, Trash2, Bot, User, Building, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  CustomFieldDefinition,
  FIELD_TYPE_LABELS,
  APPLIES_TO_LABELS,
  SUPPLIED_BY_LABELS,
} from '@wove/api'
import { CustomFieldAppliesTo } from '@wove/common/customFields'

interface CustomFieldsTableProps {
  fields: CustomFieldDefinition[]
  onEdit: (field: CustomFieldDefinition) => void
  onDelete: (field: CustomFieldDefinition) => void
  onReorder: (appliesTo: CustomFieldAppliesTo, reorderedFields: CustomFieldDefinition[]) => void
  isLoading?: boolean
}

function SortableRow({
  field,
  onEdit,
  onDelete,
}: {
  field: CustomFieldDefinition
  onEdit: (field: CustomFieldDefinition) => void
  onDelete: (field: CustomFieldDefinition) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      <td className="px-2 py-4 whitespace-nowrap">
        <button
          className="cursor-grab hover:bg-gray-100 p-1 rounded"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </button>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">{field.fieldName}</div>
          <div className="text-sm text-gray-500 font-mono">{field.fieldKey}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {FIELD_TYPE_LABELS[field.fieldType as keyof typeof FIELD_TYPE_LABELS]}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {field.suppliedBy === 'customer' ? (
            <Building className="h-4 w-4 text-blue-500 mr-1" />
          ) : (
            <User className="h-4 w-4 text-green-500 mr-1" />
          )}
          <span className="text-sm text-gray-900">
            {SUPPLIED_BY_LABELS[field.suppliedBy as keyof typeof SUPPLIED_BY_LABELS]}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {field.isAiExtractable ? (
          <div className="flex items-center">
            <Bot className="h-4 w-4 text-purple-500 mr-1" />
            <span className="text-sm text-purple-600">Enabled</span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Disabled</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(field)}
            className="text-blue-600 hover:text-blue-900"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(field)}
            className="text-red-600 hover:text-red-900"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  )
}

export default function CustomFieldsTable({
  fields,
  onEdit,
  onDelete,
  onReorder,
  isLoading,
}: CustomFieldsTableProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (fields.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">
          <div className="text-lg font-medium mb-2">No custom fields found</div>
          <div className="text-sm">Create your first custom field to get started</div>
        </div>
      </div>
    )
  }

  const rfqFields = fields.filter((field) => field.appliesTo === 'rfq')
  const shipmentFields = fields.filter((field) => field.appliesTo === 'shipment')
  const containerFields = fields.filter((field) => field.appliesTo === 'container')

  const handleDragEnd = (
    event: DragEndEvent,
    appliesTo: CustomFieldAppliesTo,
    sectionFields: CustomFieldDefinition[],
  ) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sectionFields.findIndex((field) => field.id === active.id)
      const newIndex = sectionFields.findIndex((field) => field.id === over.id)

      const reorderedFields = arrayMove(sectionFields, oldIndex, newIndex)
      onReorder(appliesTo, reorderedFields)
    }
  }

  const renderFieldsSection = (
    sectionFields: CustomFieldDefinition[],
    title: string,
    appliesTo: CustomFieldAppliesTo,
  ) => {
    if (sectionFields.length === 0) return null

    const sortedFields = [...sectionFields].sort((a, b) => a.fieldOrder - b.fieldOrder)

    return (
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => handleDragEnd(event, appliesTo, sortedFields)}
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {/* Drag handle column */}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Supplied By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <SortableContext
                  items={sortedFields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {sortedFields.map((field) => (
                    <SortableRow key={field.id} field={field} onEdit={onEdit} onDelete={onDelete} />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>
      </div>
    )
  }

  return (
    <div>
      {renderFieldsSection(rfqFields, 'RFQ Custom Fields', CustomFieldAppliesTo.RFQ)}
      {renderFieldsSection(shipmentFields, 'Shipment Custom Fields', CustomFieldAppliesTo.SHIPMENT)}
      {renderFieldsSection(
        containerFields,
        'Container Custom Fields',
        CustomFieldAppliesTo.CONTAINER,
      )}
    </div>
  )
}
